// src/features/arrivals/components/LiveStrip.tsx
//
// «Ahora mismo» — the live strip of mockup 01: one line of per-channel
// counts, then every active session at full width with its channel on the
// card. Equal columns per channel left three of four empty on a normal day
// and squeezed the one session that mattered into a quarter of the width.
// Sessions needing help come first, then the stuck ones, then the longest
// in their step. The stuck flag is the ONE amber accent of this view;
// «Necesita ayuda» uses the error colour. Reused as-is by the wall screen
// (mockup 02).
import React from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { useTranslate } from 'react-admin';
import { getChannelMeta, getChannelShortLabelKey } from '@/design/registry/channelRegistry';
import { displayIdentifier, formatStuckFlag } from '@/features/reservations/lib/reservationFormat';
import { getSectionMeta } from '@/design/registry/sectionRegistry';
import type { ArrivalsLiveGroup, LiveChannel, ReservationState } from '@/types/reservationState';

export interface LiveStripProps {
  groups: ArrivalsLiveGroup[];
  onSelect?: (reservation: ReservationState) => void;
  dense?: boolean;
  /** No card of its own: the strip sits inside a parent surface (Llegadas de hoy summary band). */
  bare?: boolean;
}

const urgency = (session: ReservationState): number =>
  session.live.needsHelp ? 2 : session.live.stuck ? 1 : 0;

const bySeverity = (a: ReservationState, b: ReservationState): number =>
  urgency(b) - urgency(a) || (b.live.minutesInSection ?? 0) - (a.live.minutesInSection ?? 0);

const SessionCard: React.FC<{ session: ReservationState; channel: LiveChannel; onSelect?: () => void; dense?: boolean }> = ({
  session,
  channel,
  onSelect,
  dense,
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const channelMeta = getChannelMeta(channel);
  const sectionMeta = getSectionMeta(session.live.section);
  const isAmber = session.live.stuck && !session.live.needsHelp;

  return (
    <Box
      onClick={onSelect}
      sx={{
        p: dense ? 1 : 1.25,
        borderRadius: theme.custom?.radius?.small ?? '8px',
        border: `1px solid ${theme.palette.divider}`,
        cursor: onSelect ? 'pointer' : 'default',
        '&:hover': onSelect ? { backgroundColor: theme.palette.action.hover } : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary', mb: 0.25 }}>
        <channelMeta.Icon sx={{ fontSize: 14 }} />
        <Typography variant="caption" noWrap>{translate(getChannelShortLabelKey(channel))}</Typography>
      </Stack>
      <Typography variant="body2" fontWeight={700} noWrap>
        {displayIdentifier(session)}
        {session.roomNumber ? ` · ${session.roomNumber}` : ''}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: session.live.needsHelp ? 'error.main' : (isAmber ? 'warning.main' : 'text.secondary'), fontWeight: isAmber || session.live.needsHelp ? 700 : 400 }}
      >
        {session.live.needsHelp
          ? translate('resources.arrivals.needsHelp')
          : isAmber
            ? formatStuckFlag(session.live.minutesInSection, session.live.section, translate)
            : `${sectionMeta ? translate(sectionMeta.labelKey) : ''} · ${session.live.minutesInSection ?? 0} min`}
      </Typography>
    </Box>
  );
};

export const LiveStrip: React.FC<LiveStripProps> = ({ groups, onSelect, dense, bare }) => {
  const translate = useTranslate();
  const sessions = groups
    .flatMap((group) => group.sessions.map((session) => ({ session, channel: group.channel })))
    .sort((a, b) => bySeverity(a.session, b.session));

  return (
    <Paper sx={bare ? { p: 0, mb: 0, boxShadow: 'none', border: 0, backgroundColor: 'transparent' } : { p: dense ? 1.5 : 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', columnGap: 3, rowGap: 0.5, mb: 1.25 }}>
        {/* Neutral eyebrow, like WidgetCard's — teal marks one thing at a time
            (rule 1), not a section label repeated on every screen. */}
        <Typography variant="overline">{translate('resources.arrivals.liveStrip.title')}</Typography>
        {groups.map((group) => {
          const meta = getChannelMeta(group.channel);
          const count = group.sessions.length;
          return (
            <Stack key={group.channel} direction="row" alignItems="center" spacing={0.75} sx={{ color: count ? 'text.primary' : 'text.secondary' }}>
              <meta.Icon sx={{ fontSize: 16 }} />
              <Typography variant="caption" fontWeight={count ? 700 : 400}>
                {translate(meta.labelKey)} · {count}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      {sessions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{translate('resources.arrivals.liveStrip.empty')}</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 1.5 }}>
          {sessions.map(({ session, channel }) => (
            <SessionCard
              key={session.id}
              session={session}
              channel={channel}
              dense={dense}
              onSelect={onSelect ? () => onSelect(session) : undefined}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default LiveStrip;
